/* eslint-disable @typescript-eslint/no-unused-vars */
// Location: src/components/admin/BuzzAgentGameControls.tsx
// Purpose: Buzz Agent Game Controls with create, start, stop functionality
// Scope: Complete game period management similar to Social Profile Game

import React, { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@/convex/_generated/api';
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
	Play,
	Pause,
	StopCircle,
	Users,
	Settings,
	Clock,
	Trophy,
	Zap,
	AlertCircle,
	RefreshCw,
	DollarSign,
	Plus,
} from 'lucide-react';
import { format, addDays, addWeeks, addMonths } from 'date-fns';
import { toast } from '@/components/ui/use-toast';

interface BuzzAgentGameControlsProps {
	className?: string;
}

export function BuzzAgentGameControls({
	className,
}: BuzzAgentGameControlsProps) {
	const [isCreatingPeriod, setIsCreatingPeriod] = useState(false);
	const [periodSettings, setPeriodSettings] = useState({
		name: '',
		description: '',
		duration: 'week', // week, month, custom
		customDays: 7,
		minAgents: 10,
		maxAgents: 1000,
		prizePool: 1000,
		autoStart: false,
	});

	// Queries
	const currentPeriod = useQuery(
		api.buzzAgentGamePeriods.getCurrentGamePeriod
	);
	const gamePeriods = useQuery(api.buzzAgentGamePeriods.getGamePeriods, {
		limit: 10,
	});
	const isRecruitmentOpen = useQuery(
		api.buzzAgentGamePeriods.isRecruitmentOpen
	);

	// Mutations
	const createPeriod = useMutation(api.buzzAgentGamePeriods.createGamePeriod);
	const updateStatus = useMutation(
		api.buzzAgentGamePeriods.updateGamePeriodStatus
	);
	const forceStart = useMutation(api.buzzAgentGamePeriods.forceStartPeriod);
	const emergencyStop = useMutation(
		api.buzzAgentGamePeriods.emergencyStopPeriod
	);

	// Handlers
	const handleCreatePeriod = async () => {
		try {
			const now = new Date();
			let endDate = new Date();
			let periodType: 'weekly' | 'monthly' | 'custom' = 'custom';

			// Calculate end date and type based on duration
			if (periodSettings.duration === 'week') {
				endDate = addWeeks(now, 1);
				periodType = 'weekly';
			} else if (periodSettings.duration === 'month') {
				endDate = addMonths(now, 1);
				periodType = 'monthly';
			} else {
				endDate = addDays(now, periodSettings.customDays);
				periodType = 'custom';
			}

			// Set name if empty
			const periodName =
				periodSettings.name ||
				`Game Period - ${format(now, 'MMM d, yyyy')}`;
			const periodDescription =
				periodSettings.description || `Buzz Agent competition period`;

			await createPeriod({
				name: periodName,
				description: periodDescription,
				type: periodType,
				startDate: now.toISOString(),
				endDate: endDate.toISOString(),
				recruitmentStartDate: now.toISOString(),
				recruitmentEndDate: addDays(now, 2).toISOString(), // 2 days for recruitment
				minAgents: periodSettings.minAgents,
				maxAgents: periodSettings.maxAgents,
				prizes: {
					first: periodSettings.prizePool * 0.5, // 50% to first place
					second: periodSettings.prizePool * 0.3, // 30% to second
					third: periodSettings.prizePool * 0.2, // 20% to third
				},
			});

			toast({
				title: 'Success',
				description: 'Game period created successfully',
			});

			setIsCreatingPeriod(false);
			// Reset form
			setPeriodSettings({
				name: '',
				description: '',
				duration: 'week',
				customDays: 7,
				minAgents: 10,
				maxAgents: 1000,
				prizePool: 1000,
				autoStart: false,
			});
		} catch (error) {
			toast({
				title: 'Error',
				description: 'Failed to create game period',
				variant: 'destructive',
			});
			console.error(error);
		}
	};

	const handleStartRecruitment = async () => {
		if (!currentPeriod) return;
		try {
			await updateStatus({
				periodId: currentPeriod._id,
				status: 'recruiting',
			});
			toast({
				title: 'Success',
				description: 'Recruitment period started',
			});
		} catch (error) {
			toast({
				title: 'Error',
				description: 'Failed to start recruitment',
				variant: 'destructive',
			});
		}
	};

	const handleStartGame = async () => {
		if (!currentPeriod) return;
		try {
			await forceStart({ periodId: currentPeriod._id });
			toast({
				title: 'Success',
				description: 'Game started successfully',
			});
		} catch (error) {
			toast({
				title: 'Error',
				description: 'Failed to start game',
				variant: 'destructive',
			});
		}
	};

	const handleEndGame = async () => {
		if (!currentPeriod) return;
		try {
			await updateStatus({
				periodId: currentPeriod._id,
				status: 'ended',
			});
			toast({
				title: 'Success',
				description: 'Game ended successfully',
			});
		} catch (error) {
			toast({
				title: 'Error',
				description: 'Failed to end game',
				variant: 'destructive',
			});
		}
	};

	const handlePauseGame = async () => {
		if (!currentPeriod) return;
		try {
			await updateStatus({
				periodId: currentPeriod._id,
				status: 'paused',
			});
			toast({
				title: 'Success',
				description: 'Game paused',
			});
		} catch (error) {
			toast({
				title: 'Error',
				description: 'Failed to pause game',
				variant: 'destructive',
			});
		}
	};

	const handleResumeGame = async () => {
		if (!currentPeriod) return;
		try {
			await updateStatus({
				periodId: currentPeriod._id,
				status: 'active',
			});
			toast({
				title: 'Success',
				description: 'Game resumed',
			});
		} catch (error) {
			toast({
				title: 'Error',
				description: 'Failed to resume game',
				variant: 'destructive',
			});
		}
	};

	const handleEmergencyStop = async () => {
		if (!currentPeriod) return;
		if (
			!confirm(
				'Are you sure? This will immediately stop the game and cannot be undone.'
			)
		)
			return;

		try {
			await emergencyStop({ periodId: currentPeriod._id });
			toast({
				title: 'Success',
				description: 'Emergency stop executed',
			});
		} catch (error) {
			toast({
				title: 'Error',
				description: 'Failed to execute emergency stop',
				variant: 'destructive',
			});
		}
	};

	const getStatusColor = (status?: string) => {
		switch (status) {
			case 'setup':
				return 'bg-gray-500';
			case 'recruiting':
				return 'bg-blue-500';
			case 'active':
				return 'bg-green-500';
			case 'paused':
				return 'bg-yellow-500';
			case 'ended':
				return 'bg-purple-500';
			case 'archived':
				return 'bg-gray-400';
			default:
				return 'bg-gray-400';
		}
	};

	const getStatusIcon = (status?: string) => {
		switch (status) {
			case 'setup':
				return Settings;
			case 'recruiting':
				return Users;
			case 'active':
				return Play;
			case 'paused':
				return Pause;
			case 'ended':
				return Trophy;
			case 'archived':
				return StopCircle;
			default:
				return AlertCircle;
		}
	};

	return (
		<div className={cn('space-y-6', className)}>
			{/* Main Controls Card */}
			<Card className='border-purple-500/20'>
				<CardHeader>
					<div className='flex items-center justify-between'>
						<div>
							<CardTitle className='text-xl flex items-center gap-2'>
								<Zap className='h-5 w-5 text-purple-500' />
								Buzz Agent Game Settings
							</CardTitle>
							{/* <CardDescription>
								Game periods, control lifecycle, and status
							</CardDescription> */}
						</div>
						{!currentPeriod ||
						['ended', 'archived'].includes(currentPeriod.status) ? (
							<Button
								onClick={() => setIsCreatingPeriod(true)}
								className='bg-gradient-to-r from-purple-500 to-pink-600'
							>
								<Plus className='h-4 w-4 mr-2' />
								Create New Period
							</Button>
						) : null}
					</div>
				</CardHeader>
				<CardContent>
					{currentPeriod ? (
						<div className='space-y-6'>
							{/* Current Period Info */}
							<div className='flex items-center justify-between p-4 rounded-lg bg-gray-900/50 border border-gray-800'>
								<div className='flex items-center gap-4'>
									{React.createElement(
										getStatusIcon(currentPeriod.status),
										{
											className:
												'h-5 w-5 text-purple-500',
										}
									)}
									<div>
										<p className='font-medium'>
											{currentPeriod.name}
										</p>
										<p className='text-sm text-muted-foreground'>
											{currentPeriod.description}
										</p>
									</div>
								</div>
								<Badge
									className={cn(
										'text-white border-0 px-3 py-1',
										getStatusColor(currentPeriod.status)
									)}
								>
									{currentPeriod.status?.toUpperCase()}
								</Badge>
							</div>

							{/* Period Details */}
							<div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
								<div className='space-y-1'>
									<p className='text-sm text-muted-foreground'>
										Start Date
									</p>
									<p className='font-medium'>
										{format(
											new Date(currentPeriod.startDate),
											'MMM d, yyyy'
										)}
									</p>
								</div>
								<div className='space-y-1'>
									<p className='text-sm text-muted-foreground'>
										End Date
									</p>
									<p className='font-medium'>
										{format(
											new Date(currentPeriod.endDate),
											'MMM d, yyyy'
										)}
									</p>
								</div>
								<div className='space-y-1'>
									<p className='text-sm text-muted-foreground'>
										Prize Pool
									</p>
									<p className='font-medium flex items-center'>
										<DollarSign className='h-4 w-4' />
										{(currentPeriod.prizes?.first || 0) +
											(currentPeriod.prizes?.second ||
												0) +
											(currentPeriod.prizes?.third ||
												0)}{' '}
										KARMA
									</p>
								</div>
								<div className='space-y-1'>
									<p className='text-sm text-muted-foreground'>
										Recruitment
									</p>
									<p className='font-medium flex items-center gap-2'>
										{isRecruitmentOpen ? (
											<>
												<span className='h-2 w-2 bg-green-500 rounded-full animate-pulse' />
												Open
											</>
										) : (
											<>
												<span className='h-2 w-2 bg-red-500 rounded-full' />
												Closed
											</>
										)}
									</p>
								</div>
							</div>

							{/* Control Buttons */}
							<div className='flex flex-wrap gap-3'>
								{currentPeriod.status === 'setup' && (
									<Button
										onClick={handleStartRecruitment}
										variant='outline'
										className='border-blue-500/50 hover:bg-blue-500/10'
									>
										<Users className='h-4 w-4 mr-2' />
										Start Recruitment
									</Button>
								)}

								{currentPeriod.status === 'recruiting' && (
									<Button
										onClick={handleStartGame}
										variant='outline'
										className='border-green-500/50 hover:bg-green-500/10'
									>
										<Play className='h-4 w-4 mr-2' />
										Start Game
									</Button>
								)}

								{currentPeriod.status === 'active' && (
									<>
										<Button
											onClick={handlePauseGame}
											variant='outline'
											className='border-yellow-500/50 hover:bg-yellow-500/10'
										>
											<Pause className='h-4 w-4 mr-2' />
											Pause Game
										</Button>
										<Button
											onClick={handleEndGame}
											variant='outline'
											className='border-purple-500/50 hover:bg-purple-500/10'
										>
											<Trophy className='h-4 w-4 mr-2' />
											End Game
										</Button>
									</>
								)}

								{currentPeriod.status === 'paused' && (
									<Button
										onClick={handleResumeGame}
										variant='outline'
										className='border-green-500/50 hover:bg-green-500/10'
									>
										<RefreshCw className='h-4 w-4 mr-2' />
										Resume Game
									</Button>
								)}

								{['active', 'paused', 'recruiting'].includes(
									currentPeriod.status
								) && (
									<Button
										onClick={handleEmergencyStop}
										variant='destructive'
										className='ml-auto'
									>
										<StopCircle className='h-4 w-4 mr-2' />
										Emergency Stop
									</Button>
								)}
							</div>
						</div>
					) : (
						<div className='text-center py-8'>
							<Zap className='h-12 w-12 text-purple-500/30 mx-auto mb-4' />
							<p className='text-lg font-medium mb-2'>
								No Active Game Period
							</p>
							<p className='text-sm text-muted-foreground mb-4'>
								Create a new game period to start the Buzz Agent
								competition
							</p>
							<Button
								onClick={() => setIsCreatingPeriod(true)}
								className='bg-gradient-to-r from-purple-500 to-pink-600'
							>
								<Plus className='h-4 w-4 mr-2' />
								Create First Game Period
							</Button>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Game Period History */}
			{gamePeriods && gamePeriods.length > 0 && (
				<Card>
					<CardHeader>
						<CardTitle className='flex items-center gap-2'>
							<Clock className='h-5 w-5 text-blue-500' />
							Game Period History
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className='space-y-2'>
							{gamePeriods.map(
								// eslint-disable-next-line @typescript-eslint/no-explicit-any
								(period: any) => (
									<div
										key={period._id}
										className='flex items-center justify-between p-3 rounded-lg bg-gray-900/50 border border-gray-800 hover:border-gray-700 transition-colors'
									>
										<div>
											<p className='font-medium'>
												{period.name}
											</p>
											<p className='text-xs text-muted-foreground'>
												{format(
													new Date(period.startDate),
													'MMM d'
												)}{' '}
												-{' '}
												{format(
													new Date(period.endDate),
													'MMM d, yyyy'
												)}
											</p>
										</div>
										<div className='flex items-center gap-3'>
											{period.prizes && (
												<span className='text-sm text-muted-foreground'>
													{(period.prizes.first ||
														0) +
														(period.prizes.second ||
															0) +
														(period.prizes.third ||
															0)}{' '}
													KARMA
												</span>
											)}
											<Badge
												variant='outline'
												className={cn(
													'text-xs',
													period.status ===
														'active' &&
														'border-green-500 text-green-500',
													period.status === 'ended' &&
														'border-purple-500 text-purple-500',
													period.status ===
														'recruiting' &&
														'border-blue-500 text-blue-500'
												)}
											>
												{period.status}
											</Badge>
										</div>
									</div>
								)
							)}
						</div>
					</CardContent>
				</Card>
			)}

			{/* Create New Period Modal */}
			{isCreatingPeriod && (
				<div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
					<Card className='w-full max-w-2xl'>
						<CardHeader>
							<CardTitle>Create New Game Period</CardTitle>
							<CardDescription>
								Configure the settings for the new Buzz Agent
								game period
							</CardDescription>
						</CardHeader>
						<CardContent className='space-y-4'>
							<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
								<div className='space-y-2'>
									<Label>Period Name</Label>
									<Input
										value={periodSettings.name}
										onChange={(e) =>
											setPeriodSettings({
												...periodSettings,
												name: e.target.value,
											})
										}
										placeholder='e.g., Week 1 Competition'
									/>
								</div>
								<div className='space-y-2'>
									<Label>Duration</Label>
									<select
										className='w-full px-3 py-2 bg-gray-900 border border-gray-800 rounded-md'
										value={periodSettings.duration}
										onChange={(e) =>
											setPeriodSettings({
												...periodSettings,
												duration: e.target.value,
											})
										}
									>
										<option value='week'>1 Week</option>
										<option value='month'>1 Month</option>
										<option value='custom'>
											Custom Days
										</option>
									</select>
								</div>
								{periodSettings.duration === 'custom' && (
									<div className='space-y-2'>
										<Label>Number of Days</Label>
										<Input
											type='number'
											value={periodSettings.customDays}
											onChange={(e) =>
												setPeriodSettings({
													...periodSettings,
													customDays: parseInt(
														e.target.value
													),
												})
											}
											min='1'
											max='90'
										/>
									</div>
								)}
								<div className='space-y-2'>
									<Label>Prize Pool (KARMA)</Label>
									<Input
										type='number'
										value={periodSettings.prizePool}
										onChange={(e) =>
											setPeriodSettings({
												...periodSettings,
												prizePool: parseInt(
													e.target.value
												),
											})
										}
										min='0'
									/>
								</div>
								<div className='space-y-2'>
									<Label>Min Agents</Label>
									<Input
										type='number'
										value={periodSettings.minAgents}
										onChange={(e) =>
											setPeriodSettings({
												...periodSettings,
												minAgents: parseInt(
													e.target.value
												),
											})
										}
										min='1'
									/>
								</div>
								<div className='space-y-2'>
									<Label>Max Agents</Label>
									<Input
										type='number'
										value={periodSettings.maxAgents}
										onChange={(e) =>
											setPeriodSettings({
												...periodSettings,
												maxAgents: parseInt(
													e.target.value
												),
											})
										}
										min='1'
									/>
								</div>
							</div>

							<div className='space-y-2'>
								<Label>Description</Label>
								<Input
									value={periodSettings.description}
									onChange={(e) =>
										setPeriodSettings({
											...periodSettings,
											description: e.target.value,
										})
									}
									placeholder='Describe this game period...'
								/>
							</div>

							<div className='flex items-center space-x-2'>
								<input
									type='checkbox'
									id='autoStart'
									checked={periodSettings.autoStart}
									onChange={(e) =>
										setPeriodSettings({
											...periodSettings,
											autoStart: e.target.checked,
										})
									}
									className='rounded border-gray-300'
								/>
								<Label htmlFor='autoStart'>
									Auto-start when minimum agents reached
								</Label>
							</div>

							<div className='flex justify-end gap-3 pt-4'>
								<Button
									variant='outline'
									onClick={() => setIsCreatingPeriod(false)}
								>
									Cancel
								</Button>
								<Button
									onClick={handleCreatePeriod}
									className='bg-gradient-to-r from-purple-500 to-pink-600'
								>
									Create Period
								</Button>
							</div>
						</CardContent>
					</Card>
				</div>
			)}
		</div>
	);
}
